<script setup lang="ts">
import { ArrowLeft, Loader2, Search, Users } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

import ConnectionErrorState from '@/components/ConnectionErrorState.vue';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import AzureGroupCard from '@/components/user-groups/AzureGroupCard.vue';
import AzureGroupMembersView from '@/components/user-groups/AzureGroupMembersView.vue';
import ImportAllAzureGroupsButton from '@/components/user-groups/ImportAllAzureGroupsButton.vue';
import {
    type AzureGroup,
    getAzureGroups,
    type UserGroup
} from '@/services/user_groups';
import {
    INVALID_SECRET_ADMIN_HINT,
    useMicrosoftConnectionStore
} from '@/stores/microsoftConnectionStore';

interface Props {
    open: boolean;
    tenantId: string;
    userGroups: UserGroup[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
    'update:open': [value: boolean];
    'group-imported': [];
}>();

const groups = ref<AzureGroup[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const currentView = ref<'groups' | 'members'>('groups');
const selectedGroup = ref<AzureGroup | null>(null);
const searchQuery = ref('');

const microsoftConnectionStore = useMicrosoftConnectionStore();
const isSecretInvalid = computed(
    () => microsoftConnectionStore.health?.status === 'invalid_secret'
);

const isOpen = computed({
    get: () => props.open,
    set: (value: boolean) => emit('update:open', value)
});

const filteredGroups = computed(() => {
    if (!searchQuery.value.trim()) {
        return groups.value;
    }

    const query = searchQuery.value.toLowerCase();
    return groups.value.filter((group) => {
        const nameMatch = group.displayName.toLowerCase().includes(query);
        const descriptionMatch = group.description
            ?.toLowerCase()
            .includes(query);
        return nameMatch || descriptionMatch;
    });
});

const allAzureGroupsImported = computed(() => {
    if (groups.value.length === 0) {
        return true; // If no Azure groups, disable the button
    }

    // Get all azure_group_ids from imported user groups
    const importedAzureGroupIds = new Set(
        props.userGroups
            .filter((ug) => ug.azure_group_id)
            .map((ug) => ug.azure_group_id)
    );

    // Check if all Azure groups have been imported
    return groups.value.every((azureGroup) =>
        importedAzureGroupIds.has(azureGroup.id)
    );
});

const loadAzureGroups = async () => {
    if (!props.tenantId) return;

    loading.value = true;
    error.value = null;

    try {
        groups.value = await getAzureGroups(props.tenantId);
    } catch (err) {
        console.error('Error loading Azure groups:', err);
        error.value = 'Failed to load Azure AD groups. Please try again.';
        // The backend does not name the cause; the health check tells a revoked secret apart.
        void microsoftConnectionStore.refresh();
    } finally {
        loading.value = false;
    }
};

const handleGroupImported = () => {
    emit('group-imported');
};

const handleViewMembers = (group: AzureGroup) => {
    selectedGroup.value = group;
    currentView.value = 'members';
};

const handleBackToGroups = () => {
    currentView.value = 'groups';
    selectedGroup.value = null;
};

// Watch for dialog open
watch(
    () => props.open,
    (isOpen) => {
        if (isOpen) {
            loadAzureGroups();
            // Reset to groups view when dialog opens
            currentView.value = 'groups';
            selectedGroup.value = null;
            searchQuery.value = '';
        }
    }
);
</script>

<template>
    <Dialog v-model:open="isOpen">
        <DialogContent
            class="max-w-3xl h-[90vh] flex flex-col py-8 overflow-hidden"
        >
            <!-- Groups View Header -->
            <DialogHeader v-if="currentView === 'groups'">
                <div class="flex items-center justify-between pr-4">
                    <div>
                        <DialogTitle> Azure AD Groups </DialogTitle>
                        <DialogDescription>
                            View all groups from your Azure Active Directory
                        </DialogDescription>
                    </div>
                    <ImportAllAzureGroupsButton
                        v-if="!loading && !error && groups.length > 0"
                        :tenant-id="tenantId"
                        :disabled="allAzureGroupsImported"
                        @import-completed="handleGroupImported"
                    />
                </div>
            </DialogHeader>

            <!-- Members View Header -->
            <div
                v-else-if="currentView === 'members' && selectedGroup"
                class="flex items-center gap-3 mb-6"
            >
                <!-- Back button -->
                <Button
                    variant="ghost"
                    size="sm"
                    @click="handleBackToGroups"
                    class="flex items-center gap-2 mt-1"
                >
                    <ArrowLeft class="w-4 h-4" />
                </Button>

                <!-- Group info -->
                <div class="flex-1">
                    <h2
                        class="text-lg font-semibold leading-none tracking-tight"
                    >
                        {{ selectedGroup.displayName }}
                    </h2>
                    <p
                        v-if="selectedGroup.description"
                        class="text-sm text-muted-foreground mt-1.5"
                    >
                        {{ selectedGroup.description }}
                    </p>
                </div>
            </div>

            <div class="flex-1 min-h-0 flex flex-col">
                <!-- Groups List View -->
                <div
                    v-if="currentView === 'groups'"
                    class="flex flex-col h-full min-h-0"
                >
                    <!-- Loading State -->
                    <div
                        v-if="loading"
                        class="flex items-center justify-center h-32"
                    >
                        <div class="text-center">
                            <Loader2
                                class="w-8 h-8 animate-spin mx-auto mb-2 text-muted-foreground"
                            />
                            <p class="text-sm text-muted-foreground">
                                Loading Azure AD groups...
                            </p>
                        </div>
                    </div>

                    <!-- Error State -->
                    <div
                        v-else-if="error"
                        class="flex flex-1 items-center justify-center px-8 pb-16"
                    >
                        <ConnectionErrorState
                            :title="
                                isSecretInvalid
                                    ? 'Azure AD connection unavailable'
                                    : 'Could not load Azure AD groups'
                            "
                            :detail="
                                isSecretInvalid ? INVALID_SECRET_ADMIN_HINT : error
                            "
                            :can-retry="!isSecretInvalid"
                            @retry="loadAzureGroups"
                        />
                    </div>

                    <!-- No Groups State -->
                    <div
                        v-else-if="groups.length === 0"
                        class="flex items-center justify-center h-64"
                    >
                        <div class="text-center">
                            <Users
                                class="w-12 h-12 mx-auto mb-2 text-muted-foreground"
                            />
                            <p class="text-sm text-muted-foreground">
                                No Azure AD groups found
                            </p>
                        </div>
                    </div>

                    <!-- Search and Groups List -->
                    <template v-else>
                        <!-- Search Input -->
                        <div class="mb-4 relative pr-4">
                            <Search
                                class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground"
                            />
                            <Input
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search groups by name or description..."
                                class="pl-9"
                            />
                        </div>

                        <!-- No Search Results State -->
                        <div
                            v-if="filteredGroups.length === 0"
                            class="flex items-center justify-center h-64"
                        >
                            <div class="text-center">
                                <Search
                                    class="w-12 h-12 mx-auto mb-2 text-muted-foreground"
                                />
                                <p class="text-sm text-muted-foreground">
                                    No groups match your search
                                </p>
                            </div>
                        </div>

                        <!-- Groups List -->
                        <ScrollArea v-else class="pr-4 h-[55dvh] md:h-[60dvh]">
                            <div class="space-y-4">
                                <AzureGroupCard
                                    v-for="group in filteredGroups"
                                    :key="group.id"
                                    :group="group"
                                    :tenant-id="tenantId"
                                    :user-groups="userGroups"
                                    @group-imported="handleGroupImported"
                                    @view-members="handleViewMembers(group)"
                                />
                            </div>
                        </ScrollArea>
                    </template>
                </div>

                <!-- Members View -->
                <div
                    v-else-if="currentView === 'members' && selectedGroup"
                    class="flex-1 min-h-0"
                >
                    <AzureGroupMembersView
                        :group="selectedGroup"
                        :tenant-id="tenantId"
                    />
                </div>
            </div>
        </DialogContent>
    </Dialog>
</template>
